import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { Star, MapPin, Users, Calendar, Target, Building } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AboutPage = () => {
  const { t, currentLanguage } = useLanguage();
  const { settings } = useSettings();
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pages/about`);
      setPageContent(response.data);
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
    setLoading(false);
  };

  const getLocalizedText = (obj) => {
    if (!obj) return '';
    return obj[currentLanguage] || obj['tr'] || obj['en'] || '';
  };

  const stats = [
    { icon: Star, label: t('about.category'), value: t('about.categoryValue') },
    { icon: Target, label: t('about.concept'), value: t('about.conceptValue') },
    { icon: Building, label: t('about.totalArea'), value: t('about.totalAreaValue') },
    { icon: Users, label: t('about.capacity'), value: t('about.capacityValue') },
    { icon: MapPin, label: t('about.targetAudience'), value: t('about.targetAudienceValue') },
  ];

  // Default hero values
  const heroImage = pageContent?.hero_image || '/images/general/hero_bg.jpg';
  const heroTitle = getLocalizedText(pageContent?.hero_title) || t('about.subtitle');
  const heroSubtitle = getLocalizedText(pageContent?.hero_subtitle) || 'Holiday Inn Resort Bodrum West Beach';

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section
        className="relative h-[60vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url('${heroImage}')`
        }}
      >
        <div className="absolute inset-0 bg-holiday-purple/60" />
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4">{heroTitle}</h1>
          <p className="text-lg md:text-xl text-white/80">{heroSubtitle}</p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="stats-section">
        <div className="container-custom px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="w-14 h-14 md:w-16 md:h-16 bg-holiday-purple/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <stat.icon className="w-7 h-7 md:w-8 md:h-8 text-holiday-purple" />
                </div>
                <p className="text-xl md:text-2xl font-heading font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs md:text-sm text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-holiday-purple text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4">
                Holiday Inn Resort
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-4 md:mb-6">
                {t('about.title')}
              </h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 md:mb-8">
                {t('about.description')}
              </p>
              <a
                href={settings.reservation_url || "https://www.ihg.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent inline-block rounded-sm text-sm md:text-base"
                data-testid="about-cta"
              >
                {t('nav.bookNow')}
              </a>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <img
                src="/images/general/general_1.jpg"
                alt="Resort View"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img
                src="/images/general/general_3.jpg"
                alt="Pool View"
                className="rounded-lg shadow-lg mt-6 md:mt-8 w-full h-48 object-cover"
              />
              <img
                src="/images/rooms/premium_sea.jpg"
                alt="Room View"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img
                src="/images/dining/main_restaurant.jpg"
                alt="Restaurant View"
                className="rounded-lg shadow-lg mt-6 md:mt-8 w-full h-48 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Sections from Admin */}
      {pageContent?.sections?.map((section, index) => (
        <section key={section.id || index} className={`section-padding ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="container-custom px-4">
            {section.type === 'text' && (
              <div className="max-w-3xl mx-auto text-center">
                {getLocalizedText(section.title) && (
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 md:mb-6">
                    {getLocalizedText(section.title)}
                  </h2>
                )}
                {getLocalizedText(section.content) && (
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                    {getLocalizedText(section.content)}
                  </p>
                )}
              </div>
            )}
            {section.type === 'image_text' && (
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                  {getLocalizedText(section.title) && (
                    <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 md:mb-6">
                      {getLocalizedText(section.title)}
                    </h2>
                  )}
                  {getLocalizedText(section.content) && (
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                      {getLocalizedText(section.content)}
                    </p>
                  )}
                </div>
                {section.image && (
                  <div className={index % 2 !== 0 ? 'lg:order-1' : ''}>
                    <img src={section.image} alt="" className="rounded-lg shadow-lg w-full" />
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Location Section */}
      <section className="section-padding bg-white" data-testid="location-section">
        <div className="container-custom px-4">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-holiday-green text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4">
              {t('location.subtitle')}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900">
              {t('location.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            <div className="bg-gray-50 p-6 md:p-8 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">{t('location.address')}</h3>
              <p className="text-sm md:text-base text-gray-600">{t('location.addressValue')}</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">{t('location.view')}</h3>
              <p className="text-sm md:text-base text-gray-600">{t('location.viewValue')}</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">{t('location.highlights')}</h3>
              <p className="text-sm md:text-base text-gray-600">{t('location.highlightsValue')}</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">{t('location.transport')}</h3>
              <p className="text-sm md:text-base text-gray-600">{t('location.transportValue')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
